import Promise from 'bluebird'

import logError from '@utils/logError'
import { GET_VIEWS_LIST } from 'apollo/queries/Views'
import { generateSlug } from '@utils/url'

/**
 * Action names
 */
export const TRANSFORM_DATE_RANGE = 'TRANSFORM_GLOBALFILTERS_DATE_RANGE'
export const SET_DATE_RANGE = 'SET_DATE_RANGE'
export const SET_DEFAULT_DATE = 'SET_DEFAULT_DATE'
export const SET_SOURCE = 'SET_SOURCE'
export const SET_ALLOWED_SOURCES = 'SET_ALLOWED_SOURCES'
export const RESET_ALLOWED_SOURCES = 'RESET_ALLOWED_SOURCES'
export const SET_IS_REVIEWS = 'SET_IS_REVIEWS'

// Views events
export const SET_VIEW = 'SET_VIEW'
export const SET_DEFAULT_VIEW = 'SET_DEFAULT_VIEW'
export const REQ_VIEWS_LIST = 'REQ_VIEWS_LIST'
export const RES_VIEWS_LIST = 'RES_VIEWS_LIST'
export const CLEAR_VIEWS = 'CLEAR_VIEWS'

// real time
export const ENABLE_REAL_TIME = 'ENABLE_REAL_TIME'
export const DISABLE_REAL_TIME = 'DISABLE_REAL_TIME'

// feed and product, route specific
export const SET_FEED = 'FILTERS_SET_FEED'
export const RESET_FEED = 'FILTERS_RESET_FEED'
export const SET_PRODUCT = 'FILTERS_SET_PRODUCT'

// redux persist filter specific
export const FILTERS_REHYDRATE = 'persist/REHYDRATE'

// Custom action related with no Filters view
export const UPDATE_REHYDRATED = 'UPDATE_REHYDRATED'

// views
export const UPDATE_VIEWS_LIST = 'UPDATE_VIEWS_LIST'

export const SET_FILTERS_FROM_ALERT = 'SET_FILTERS_FROM_ALERT'
export const CLEAR_ALERT_ID = 'CLEAR_ALERT_ID'

/**
 * Action dispatchers
 */

export function transformDateRange(dateRange) {
  return {
    type: TRANSFORM_DATE_RANGE,
    payload: { dateRange },
  }
}

export function setDateRange(dateRange) {
  // When the end of the range is rounded to the start of that day, modify it
  // to be the END of that day instead.
  if (dateRange.end.clone().startOf('day').isSame(dateRange.end)) {
    dateRange.end.endOf('day')
  }

  return {
    type: SET_DATE_RANGE,
    payload: { dateRange },
  }
}

export function setSource(source) {
  return {
    type: SET_SOURCE,
    payload: { source },
  }
}

export function setIsReviews(product) {
  return {
    type: SET_IS_REVIEWS,
    payload: { product },
  }
}

export function setRehydrated(payload) {
  return {
    type: UPDATE_REHYDRATED,
    payload,
  }
}

export function setAllowedSources(sources) {
  return {
    type: SET_ALLOWED_SOURCES,
    payload: { sources },
  }
}

export function resetAllowedSources() {
  return { type: RESET_ALLOWED_SOURCES }
}

export function allowOnlySource(source) {
  return setAllowedSources([source])
}

export function disableSources(sources) {
  const allSources = [
    'all',
    'twitter',
    'facebook',
    'instagram',
    'youtube',
    'tiktok',
    'media',
    'review',
    'linkedin',
  ]
  return setAllowedSources(
    allSources.filter(source => !sources.includes(source)),
  )
}

export function setDefaultRange() {
  return { type: SET_DEFAULT_DATE, payload: {} }
}

/**
 * Views
 */

export function setView(viewId) {
  return {
    type: SET_VIEW,
    payload: { view: viewId },
  }
}

export function setDefaultView(viewId) {
  return {
    type: SET_DEFAULT_VIEW,
    payload: { view: viewId },
  }
}

function viewsListReq() {
  return { type: REQ_VIEWS_LIST, payload: {} }
}

function viewsListRes(views, productId) {
  return {
    type: RES_VIEWS_LIST,
    payload: { views },
    meta: { productId },
  }
}

export function updateViewsList(view) {
  return {
    type: UPDATE_VIEWS_LIST,
    payload: view,
  }
}

/**
 * Sort views, sticky first by slug name
 * @returns list of views sorted by default view first
 */
function orderViewsByDefault(views, productId) {
  return views
    .filter(view => view.active)
    .map(view => ({
      id: view.id,
      name: view.name,
      default: view.default,
      productId: productId,
      slug: generateSlug(view.name),
      themes: view.themes,
    }))
    .sort((a, b) => Number(b.default) - Number(a.default))
}

/**
 * Get views with Apollo query
 * @param {String} productId
 */
export function getViews(productId, apolloClient, setDefault = true) {
  return async dispatch => {
    dispatch(viewsListReq())

    if (!productId) return

    try {
      const {
        data: {
          getViews: { results },
        },
      } = await apolloClient.query({
        query: GET_VIEWS_LIST,
        variables: {
          productId,
        },
      })

      if (!results) {
        return
      }

      const views = orderViewsByDefault(results, productId)

      dispatch(viewsListRes(views, productId))

      if (setDefault && views.length > 0) {
        dispatch(setView(views[0].id))
      }
      return views
    } catch (err) {
      logError(err, {
        operationName: 'getViews',
        info: 'Error while fetching views from Apollo',
        inputs: {
          productId,
          setDefault,
        },
      })
    }
  }
}

/**
 * Fetch the views if we don't have it yet
 */
export function maybeFetchViews({
  product,
  apolloClient,
  options = { setDefault: false },
}) {
  return (dispatch, getState) => {
    const { lastFetchProductId, views } = getState().globalFilters.meta

    // When the last time we fetched was a different product, fetch the view
    if (lastFetchProductId !== product.id) {
      // Whenever we change product, reset the value of the selected view
      dispatch(setView(null))
      return dispatch(getViews(product.id, apolloClient, options.setDefault))
    }

    return Promise.resolve(views)
  }
}

/**
 * Clears the list of views
 */
export function clearViews() {
  return { type: CLEAR_VIEWS, payload: {} }
}

/**
 * Real time mode
 * @param {Number} interval - Interval in minutes
 */
export function enableRealTime(interval) {
  return { type: ENABLE_REAL_TIME, payload: { interval } }
}

export function disableRealTime() {
  return { type: DISABLE_REAL_TIME, payload: {} }
}

export function setFeed(feedId) {
  return { type: SET_FEED, payload: { feedId } }
}

export function setProduct(productId) {
  return { type: SET_PRODUCT, payload: { productId } }
}

export function setFiltersFromAlert(payload) {
  return { type: SET_FILTERS_FROM_ALERT, payload }
}

export function clearAlertId() {
  return { type: CLEAR_ALERT_ID, payload: {} }
}
